import isArray from 'lodash/isArray';
import { createSelector } from 'reselect';

import { isStationVoiceTrack } from '../../lib/VoiceTrackUtils';

import { setExternalAdIdentifiers, setPremiumAccessVideoStats, tryAdOpportunity } from './actions';
import * as D from './deps';
import {
  logAudioLifecycleEvent,
  logDfpImpressionStats,
  logFetchDfpStats,
  logGenerateVastStats,
  logGetAdListStats,
  logImpressionEventStats,
  logInitPlayerStats,
  logLoadGoogleImaSdk,
  logParseDfpStats,
  logRegisterVideoAdImpressionStats,
  logTapToVideo,
  logTrackingEventStats,
  logVideoEndStats,
  logVideoErrorStats,
} from './sagas/workers/Analytics';
import { adOpportunity } from './sagas/workers/Chronos';
import { ONETRUST_COOKIE_GROUP } from './constants';

export function pingImageTrackingUrl(url) {
  if (!D.Utils.Url.isValidUrl(url)) {
    return Promise.resolve(new TypeError('Invalid url supplied as image tracking url'));
  }
  const target = D.Utils.Url.secureUrl(url);
  return new Promise(resolve => {
    try {
      const img = new Image(1, 1);
      img.onload = () => {
        resolve();
      };
      img.onerror = e => {
        resolve(e);
      };
      img.src = target;
    } catch (e) {
      resolve(e);
    }
  });
}

export function pingTrackers(name, trackers) {
  return new Promise(resolveTrackers => {
    const results = [];
    if (!trackers || !trackers.length) {
      return resolveTrackers(results);
    }
    const toTrack = isArray(trackers) ? trackers : [trackers];
    const tracking = toTrack.map(url => {
      return pingImageTrackingUrl(url)
        .then(e => {
          results.push({
            name,
            url,
            status: !e,
          });
        })
        .catch(e => {
          results.push({
            name,
            url,
            reason: e.message,
            status: false,
          });
        });
    });
    resolveTrackers(Promise.all(tracking).then(() => results));
  });
}

function getIsAdRequestInFlight(state) {
  return state.ads.get('adRequestInFlight');
}

function getHasAdBeenRequestedForToken(state, token) {
  return state.ads.get('adRequestInFlightToken') === token;
}

function getIsDisplayAdAllowedOnPage(state) {
  return D.getInDomain('displayAds.utils.getIsDisplayAdAllowedOnPage', false)(state);
}

function getIsActiveDisplayAdABillboard(state) {
  return D.getInDomain('displayAds.utils.getIsActiveDisplayAdABillboard', false)(state);
}

function getVideoAdShowing(state) {
  return D.getInDomain('videoAds.utils.getVideoAdShowing', false)(state);
}

export function getUIDExpiryDate(state) {
  return state.ads.get('uidExpiryDate');
}

export function getUID(state) {
  return state.ads.get('uid');
}

/**
 * Check if right rail should be shown.
 *
 * @returns {boolean} Returns true if display ad right rail should be visible, false otherwise
 */
export const getIsRightRailActive = createSelector(
  state => state.ui,
  D.listenerIsBasic,
  D.isAnonymousListener,
  getIsDisplayAdAllowedOnPage,
  getIsActiveDisplayAdABillboard,
  getVideoAdShowing,
  D.Utils.Listener.isStationPlayback,
  (
    ui,
    isListenerBasic,
    isListenerAnonymous,
    isDisplayAdAllowedOnPage,
    isActiveDisplayAdABillboard,
    isVideoAdShowing,
    isStationPlayback,
  ) => {
    if (
      isListenerBasic &&
      !isListenerAnonymous &&
      isDisplayAdAllowedOnPage &&
      !isActiveDisplayAdABillboard &&
      !isVideoAdShowing &&
      isStationPlayback
    ) {
      return ui.get(D.Constants.Screen.ScreenKeys.IS_SCREEN_SMALL);
    }
    return false;
  },
);

/**
 * Checks whether or not a UI element should be narrower, to leave room for a display ad (now playing wrapper, backstage)
 * this different sizing is only needed if the right rail is not active and already taking up the visual space for ads
 *
 * @returns {boolean} Returns true if this UI element needs to be more narrow for display ads, false otherwise
 */
export const getIsElementCenteredWithAds = createSelector(
  state => state.ui,
  D.listenerIsBasic,
  D.isAnonymousListener,
  getIsDisplayAdAllowedOnPage,
  getIsActiveDisplayAdABillboard,
  getVideoAdShowing,
  D.Utils.UIState.getIsSearchOpen,
  (
    ui,
    isListenerBasic,
    isListenerAnonymous,
    isDisplayAdAllowedOnPage,
    isActiveDisplayAdABillboard,
    isVideoAdShowing,
    isSearchOpen,
  ) => {
    if (
      isListenerBasic &&
      !isListenerAnonymous &&
      isDisplayAdAllowedOnPage &&
      !isActiveDisplayAdABillboard &&
      !isVideoAdShowing &&
      !isSearchOpen
    ) {
      return !ui.get(D.Constants.Screen.ScreenKeys.IS_SCREEN_SMALL);
    }
    return false;
  },
);

export const getExternalAdIdentifiers = createSelector(
  state => state.ads,
  ads => ads.get('extIds'),
);

export function isAudioAd(state, track) {
  return D.getInDomain('audioAds.utils.isAudioAd', false)(track);
}

export function getSourceIdFromStationId(stationId) {
  if (!stationId) {
    return '';
  }

  return `ST:0:${stationId}`;
}
/**
 *
 * This function checks if user consent to storing onetrust ads cookies
 */
export const isCookieConsented = () => {
  try {
    return (window.OptanonActiveGroups ?? '')
      .toString()
      .split(',')
      .includes(ONETRUST_COOKIE_GROUP.AD_GROUP);
  } catch {
    return false;
  }
};

export const externalUtils = {
  adOpportunity,
  logAudioLifecycleEvent,
  logDfpImpressionStats,
  logFetchDfpStats,
  logGenerateVastStats,
  logGetAdListStats,
  logImpressionEventStats,
  logInitPlayerStats,
  logLoadGoogleImaSdk,
  logParseDfpStats,
  logRegisterVideoAdImpressionStats,
  logTrackingEventStats,
  logVideoEndStats,
  logVideoErrorStats,
  logTapToVideo,
  getExternalAdIdentifiers,
  setPremiumAccessVideoStats,
  getHasAdBeenRequestedForToken,
  getIsAdRequestInFlight,
  getIsElementCenteredWithAds,
  getIsRightRailActive,
  pingImageTrackingUrl,
  pingTrackers,
  setExternalAdIdentifiers,
  tryAdOpportunity,
  isCookieConsented,
  isStationVoiceTrack,
  isAudioAd,
  getSourceIdFromStationId,
  getUIDExpiryDate,
  getUID,
};
